import { PriceSentiment, SeasonLength, SiblingImpact } from "@fsl/shared";
import { RadioGroup } from "./RadioGroup.js";
import type { SurveyState } from "./types.js";

const PRICE_FSL = [
  { value: PriceSentiment.GREAT_DEAL,              label: "A great deal" },
  { value: PriceSentiment.FAIR,                    label: "Fair for what's included" },
  { value: PriceSentiment.EXPENSIVE_BUT_CONSIDER,  label: "Expensive, but I'd still consider it" },
  { value: PriceSentiment.TOO_EXPENSIVE,           label: "Too expensive — I wouldn't sign up" },
] as const;

const PRICE_CATEGORY = [
  { value: PriceSentiment.GREAT_DEAL,              label: "A great deal" },
  { value: PriceSentiment.FAIR,                    label: "Fair" },
  { value: PriceSentiment.EXPENSIVE_BUT_CONSIDER,  label: "Expensive, but worth it for the right program" },
  { value: PriceSentiment.TOO_EXPENSIVE,           label: "Too expensive — I wouldn't spend that on youth sports" },
] as const;

const SIBLING = [
  { value: SiblingImpact.YES_MEANINGFUL, label: "Yes, meaningfully" },
  { value: SiblingImpact.NOT_REALLY,     label: "Not really — we evaluate per-kid cost" },
  { value: SiblingImpact.ONE_KID,        label: "Only one kid would play" },
] as const;

const SEASON = [
  { value: SeasonLength.FULL_YEAR,   label: "Full year (12 months) — all four sports rotate across the year" },
  { value: SeasonLength.SCHOOL_YEAR, label: "School year (~8–9 months) — all four sports, summer off" },
  { value: SeasonLength.HALF_YEAR,   label: "Half year (6 months) — shorter rotation, enroll each half" },
] as const;

interface Props {
  state: SurveyState;
  patch: (p: Partial<SurveyState>) => void;
  onBack: () => void;
  onNext: () => void;
  canAdvance: boolean;
}

export function Step2({ state, patch, onBack, onNext, canAdvance }: Props): JSX.Element {
  return (
    <div className="step active" data-step="2">
      <div className="field">
        <div className="field-label">
          FSL is $97/month. How does that price point feel?
        </div>
        <RadioGroup
          name="price_fsl"
          group="price_fsl"
          value={state.priceFsl}
          onChange={(v) => patch({ priceFsl: v })}
          options={PRICE_FSL}
        />
      </div>

      <div className="field">
        <div className="field-label">
          For youth sports in general, how does $1,200 per child for a full year feel?
        </div>
        <RadioGroup
          name="price_category"
          group="price_category"
          value={state.priceCategory}
          onChange={(v) => patch({ priceCategory: v })}
          options={PRICE_CATEGORY}
        />
      </div>

      <div className="field">
        <div className="field-label">Would a sibling discount make a real difference for your family?</div>
        <RadioGroup
          name="sibling"
          group="sibling"
          value={state.sibling}
          onChange={(v) => patch({ sibling: v })}
          options={SIBLING}
        />
      </div>

      <div className="field">
        <div className="field-label">
          FSL is a multi-sport league no matter the length — which of these feels right?
        </div>
        <div className="field-hint">All four sports either way. This is just about how long the year runs.</div>
        <RadioGroup
          name="season"
          group="season"
          value={state.season}
          onChange={(v) => patch({ season: v })}
          options={SEASON}
        />
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack}>← Back</button>
        <div className="right-side">
          <button type="button" className="btn btn-primary" onClick={onNext} disabled={!canAdvance}>Done →</button>
        </div>
      </div>
    </div>
  );
}
